/**
 * One refusal, turned into one sentence an owner can act on.
 *
 * ── WHY THIS IS A HELPER AND NOT INLINE IN THE PAGE ───────────────────────────────
 *
 * Because it is the part of this feature most likely to be read in anger, and a function is
 * testable without mounting anything. The forms feature can fail in seven distinguishable ways
 * and six of them are NOT the owner's fault:
 *
 *   builder_unreachable  the form builder is down           → try again in a minute
 *   builder_refused      it answered, refusing               → ours to look at
 *   builder_malformed    the answer was a shape we do not know → ours to fix
 *   no_such_task         it cannot find this task at all    → ours: the two sides disagree about
 *                                                             how a task is named or addressed
 *   not_eligible         the NUMBER is not ready            → the verdict above says why
 *   no_form              the TASK has nothing a form can ask → a fact about the task
 *   rejected             META refused it                    → Meta's own words, plus the stage
 *   business_not_verified  META refused it because the business is not verified → OUR words
 *                          (the same sentence the verdict card uses), plus the stage, and never
 *                          Meta's own ("Integrity requirements not met." tells an owner nothing)
 *
 * The seventh is a transport failure with no server code at all.
 *
 * ── THE STAGE SENTENCE IS THE ONE THAT SAVES A SUPPORT CALL ───────────────────────
 *
 * A publish refused at `upload` means the form EXISTS on the business's WhatsApp account as an
 * unfinished draft, under a name this sentence gives her. Without that, she has an invisible
 * artefact she cannot find and every retry collides with its name. `create` is the only stage
 * that leaves nothing behind, and it says so.
 *
 * NOTHING HERE WRITES ENGLISH. Every string comes from `i18n.js`, so both languages move together.
 * Meta's own message is the one exception and is quoted rather than translated — it is Meta's
 * sentence, not ours, and paraphrasing it would lose the detail that explains the refusal.
 */

/** The server codes this helper has a sentence for. Exported so the spec can prove that. */
export const FLOW_SERVER_CODES = [
  'builder_unreachable',
  'builder_refused',
  'builder_malformed',
  'no_such_task',
  'not_eligible',
  'no_form',
  'rejected',
  'publish_failed',
  'not_a_whatsapp_cloud_inbox',
  'blank',
  // Field fixes G1: the number could not be readied for a form that loads live choices.
  'endpoint_unavailable',
  // 2026-10-04: Meta refused the publish because the business is not verified.
  'business_not_verified',
];

const SENTENCE_KEYS = {
  builder_unreachable: 'WASLO.FORMS.ERROR.BUILDER_UNREACHABLE',
  builder_refused: 'WASLO.FORMS.ERROR.BUILDER_REFUSED',
  builder_malformed: 'WASLO.FORMS.ERROR.BUILDER_MALFORMED',
  no_such_task: 'WASLO.FORMS.ERROR.NO_SUCH_TASK',
  not_eligible: 'WASLO.FORMS.ERROR.NOT_ELIGIBLE',
  no_form: 'WASLO.FORMS.ERROR.NO_FORM',
  rejected: 'WASLO.FORMS.ERROR.REJECTED',
  endpoint_unavailable: 'WASLO.FORMS.ERROR.ENDPOINT_UNAVAILABLE',
  // ONE sentence for this fact, wherever it is caught: the verdict card's. Two keys saying the
  // same thing would drift, and the owner would read two different instructions for one task.
  business_not_verified: 'WASLO.FORMS.WHY.BUSINESS_NOT_VERIFIED',
};

/** The verdict code, on a `not_eligible` refusal, that has its own sentence here. */
const BUSINESS_NOT_VERIFIED = 'business_not_verified';

const STAGE_KEYS = {
  create: 'WASLO.FORMS.ERROR.STAGE.CREATE',
  upload: 'WASLO.FORMS.ERROR.STAGE.UPLOAD',
  publish: 'WASLO.FORMS.ERROR.STAGE.PUBLISH',
};

const quoted = (t, metaError) => {
  const detail =
    typeof metaError?.message === 'string' && metaError.message.trim()
      ? metaError.message.trim()
      : null;
  return detail ? t('WASLO.FORMS.ERROR.META_SAID', { detail }) : '';
};

const stageSentence = (t, metaError) => {
  const key = STAGE_KEYS[String(metaError?.stage ?? '')];
  if (!key) return '';
  return t(key, { name: metaError?.name ?? metaError?.flow_id ?? '' });
};

/**
 * @param {Function} t the Waslo composer's `t`.
 * @param {object|null} error a `FlowApiError`, or anything at all.
 * @returns {string} one sentence, never a key and never empty.
 */
export const flowErrorSentence = (t, error) => {
  const key = SENTENCE_KEYS[String(error?.serverCode ?? '')];
  if (!key) return t('WASLO.FORMS.ERROR.GENERIC');

  // A publish refused BEFORE Meta was touched, for a business Meta has not verified: the generic
  // "this number cannot use forms" would hide the one thing she can do about it.
  if (
    error.serverCode === 'not_eligible' &&
    error.eligibility?.code === BUSINESS_NOT_VERIFIED
  ) {
    return t(SENTENCE_KEYS[BUSINESS_NOT_VERIFIED]);
  }

  // Meta refused at publish for the business, not the form. What it left behind first (a draft
  // under a name she can find), then what to do. Meta's own sentence is deliberately NOT quoted.
  // When the server deleted that draft (`draft_deleted: true`) there is nothing left behind to
  // name, and saying there is would send her looking for a form that no longer exists.
  if (error.serverCode === BUSINESS_NOT_VERIFIED) {
    const leftBehind =
      error.metaError?.draft_deleted === true
        ? ''
        : stageSentence(t, error.metaError);
    return [leftBehind, t(key)].filter(Boolean).join(' ');
  }

  if (error.serverCode !== 'rejected') return t(key);

  // A Meta refusal is up to three clauses: what happened, what it left behind, and what Meta
  // itself said. Joined with spaces and each one optional, so a silent refusal still reads as a
  // sentence rather than as a dangling fragment.
  return [t(key), stageSentence(t, error.metaError), quoted(t, error.metaError)]
    .filter(Boolean)
    .join(' ');
};

/**
 * Meta's word for a published form's state, or the one string that admits we do not know it.
 * NOTHING IS DERIVED: an absent status is `UNTOLD`, and a status Meta has newly invented is shown
 * as Meta wrote it rather than folded into the nearest one we happen to know. The sibling
 * templates screen exists because this exact thing was got wrong twice.
 */
export const flowStatusLabel = (t, status) => {
  if (typeof status !== 'string' || !status.trim()) {
    return t('WASLO.FORMS.STATUS.UNTOLD');
  }
  const key = `WASLO.FORMS.STATUS.${status.trim().toUpperCase()}`;
  const label = t(key);
  return label === key ? status.trim() : label;
};
